import React, { useState, useContext, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import apiClient from '../api/client';
import { toast } from 'sonner';
import { Heart, Search, MessageCircle, Users, Settings, LogOut, Sparkles, Crown, Eye, Mail, Edit, DollarSign, HelpCircle, BookOpen, ShoppingCart, Copy, Check, Info, Lock, Moon, Sun, Waves, Camera, Gamepad2, Menu, X } from 'lucide-react';
import { AuthContext } from '../App';
import { useEntitlements } from '../context/EntitlementsContext';
import TodayFlow from './TodayFlow';
import OnboardingTourModal from './OnboardingTourModal';
import ProfileCompleteNudgeModal from './ProfileCompleteNudgeModal';
import { LoveLinkIntroFlow } from './lovelink/LoveLinkIntroFlow.tsx';
import { LoveLinkEarningsTeaser } from './lovelink/LoveLinkEarningsTeaser.tsx';
import { calculateProfileCompletion, getCompletionMessage } from '../utils/profileCompletion';
import { resolveMainPhoto } from '../utils/profileMedia';
import { ProfileViewModal } from './profile/ProfileViewModal';
import { useProfileViewer } from '../hooks/useProfileViewer';
import { getAgeFromDob } from '../utils/ageCalculator';
import { UpgradeToEliteButton } from './billing/UpgradeToEliteButton';
import WingmanModal from './WingmanModal';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import InnerWorkCard from './InnerWorkCard';
import { normalizeList } from '../utils/normalize';
import TodaysEventsCard from './TodaysEventsCard';
import { goVerify } from '../utils/verificationNav';
import { 
  canShowProfileNudge, 
  markProfileNudgeShownThisSession, 
  recordInterruptionEvent 
} from '../utils/profileNudge';
import RetentionNextMilestoneCard from './RetentionNextMilestoneCard';
import RoomPromptBanner from './RoomPromptBanner';
import { useCommunityRhythmToday } from '../hooks/useCommunityRhythmToday';
import RecognitionBadgeCard from './RecognitionBadgeCard';
import { useArcadeInviteBadge } from '../lib/arcade/useArcadeInviteBadge';

const Dashboard = () => {
  const { user, token, logout } = useContext(AuthContext);
  const { isPremium, hasFeature, getLimit } = useEntitlements();
  const navigate = useNavigate();
  const location = useLocation();
  
  const arcadeBadge = useArcadeInviteBadge({ enabled: !!token });
  
  // P2: Profile viewer modal
  const profileViewer = useProfileViewer();
  
  const [profile, setProfile] = useState(null);
  const [matches, setMatches] = useState([]);
  const [flirts, setFlirts] = useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Navigate and close the mobile drawer in one go
  const mobileGo = (path) => {
    setMobileMenuOpen(false);
    navigate(path);
  };
  const [loungeStatus, setLoungeStatus] = useState(null);
  
  // 🎯 FIX: Profile status state machine to prevent random teleports
  // 'loading' = still fetching, 'exists' = profile found, 'missing' = true 404, 'error' = network/server error
  const [profileStatus, setProfileStatus] = useState('loading');
  
  // 🎯 Patch 7 Part 4: Unread message count
  const [unreadCount, setUnreadCount] = useState(0);

  // 🔔 Notification badge counts (unread/new)
  const [badgeCounts, setBadgeCounts] = useState({
    matches_unread: 0,
    likes_unread: 0,
    messages_unread: 0,
    flirts_unread: 0,
  });

  // 🎯 Patch B1: Onboarding tour
  const [showOnboarding, setShowOnboarding] = useState(false);

  // P1: Profile Polish - P1 profile data and completion
  const [p1Profile, setP1Profile] = useState(null);
  const [showProfileNudge, setShowProfileNudge] = useState(false);
  const p1Completion = useMemo(() => {
    return calculateProfileCompletion(p1Profile);
  }, [p1Profile]);
  
  // Unity Wingman modal
  const [showWingman, setShowWingman] = useState(false);
  
  // 🎯 Unity Flow: Daily task completion status
  const [dailyFlow, setDailyFlow] = useState(null);

  // 🎵 Community Rhythm: Today's theme
  const { theme: rhythmTheme, tagline: rhythmTagline, hasTheme: hasRhythmTheme } = useCommunityRhythmToday();

  // LoveLink Intro Flow
  const [showLoveLinkIntro, setShowLoveLinkIntro] = useState(false);
  const [loveLinkUrl, setLoveLinkUrl] = useState(null);
  
  // UI flags loaded from backend
  const [uiFlagsLoaded, setUiFlagsLoaded] = useState(false);

  // Check if user has seen onboarding tour - fetch from backend first
  useEffect(() => {
    const fetchUIFlags = async () => {
      if (!token || !user?.id) return;
      
      try {
        const response = await apiClient.get(`/me/ui-flags`);
        
        const flags = response.data;
        
        // If tour not completed on backend, show it
        if (!flags.has_completed_main_tour) {
          setShowOnboarding(true);
        }
        
        // Store in localStorage as user-scoped fallback
        try {
          localStorage.setItem(`ul_tour_completed_${user.id}`, flags.has_completed_main_tour ? '1' : '');
          localStorage.setItem(`ul_circle_seen_${user.id}`, flags.has_seen_circle_popup ? '1' : '');
        } catch (e) {
          // ignore localStorage errors
        }
        
        setUiFlagsLoaded(true);
      } catch (err) {
        console.warn('[Dashboard] Failed to fetch UI flags, using localStorage fallback:', err);
        
        // Fallback to user-scoped localStorage
        try {
          const tourCompleted = localStorage.getItem(`ul_tour_completed_${user.id}`);
          if (!tourCompleted) {
            setShowOnboarding(true);
          }
        } catch (e) {
          // ignore
        }
        setUiFlagsLoaded(true);
      }
    };
    
    fetchUIFlags();
  }, [token, user?.id]);

  // 🔔 Listen for global badge refresh (when notifications are cleared elsewhere)
  useEffect(() => {
    const refreshBadges = async () => {
      if (!token) return;
      try {
        const res = await apiClient.get(`/notifications/summary`);
        setBadgeCounts({
          matches_unread: res.data.matches_unread || 0,
          likes_unread: res.data.likes_unread || 0,
          messages_unread: res.data.messages_unread || 0,
          flirts_unread: res.data.flirts_unread || 0,
        });
      } catch (err) {
        console.log('[Dashboard] Badge refresh failed:', err);
      }
    };

    // 🚀 Direct state update (faster than re-fetch)
    const updateBadges = (summary) => {
      setBadgeCounts({
        matches_unread: summary.matches_unread || 0,
        likes_unread: summary.likes_unread || 0,
        messages_unread: summary.messages_unread || 0,
        flirts_unread: summary.flirts_unread || 0,
      });
    };

    // Register for global refresh + direct update
    window.refreshDashboardBadges = refreshBadges;
    window.updateDashboardBadges = updateBadges;

    return () => {
      delete window.refreshDashboardBadges;
      delete window.updateDashboardBadges;
    };
  }, [token]);

  const handleCloseOnboarding = async () => {
    // Persist to backend first (canonical source)
    if (token) {
      try {
        await apiClient.patch(`/me/ui-flags`, { has_completed_main_tour: true });
      } catch (err) {
        console.warn('[Dashboard] Failed to persist tour completion to backend:', err);
      }
    }
    
    // Also set user-scoped localStorage as fallback
    try {
      if (user?.id) {
        localStorage.setItem(`ul_tour_completed_${user.id}`, '1');
      }
      // Keep legacy key for backward compatibility during transition
      localStorage.setItem("unitylove_onboarding_v1", "seen");
    } catch (e) {
      // ignore
    }
    setShowOnboarding(false);
    
    // Record interruption event - prevents nudge from showing for 2 minutes
    recordInterruptionEvent();
  };
  
  // P1: Profile completion nudge - behavior-based gating, not time-based
  // Only shows if: completion <= 20%, not within 2min of interruption, 
  // not already shown this session, not dismissed in last 7 days
  useEffect(() => {
    // Skip if profile data not loaded yet
    if (!profile) return;
    
    // Use centralized gating logic
    if (!canShowProfileNudge({ completionPct: p1Completion, thresholdPct: 20 })) {
      return;
    }
    
    // Show nudge after a brief delay (gives page time to settle)
    const timer = setTimeout(() => {
      markProfileNudgeShownThisSession();
      setShowProfileNudge(true);
    }, 1500);
    
    return () => clearTimeout(timer);
  }, [profile, p1Completion]);

  // LoveLink Intro Flow - Show after main onboarding
  useEffect(() => {
    // Wait for UI flags to load from backend
    if (!uiFlagsLoaded || !profile || !user?.id) return;
    
    const checkAndShowLoveLinkIntro = async () => {
      // Check backend flags first
      try {
        const response = await apiClient.get(`/me/ui-flags`);
        
        const flags = response.data;
        
        // Only show if: tour is done AND circle popup not seen
        if (flags.has_completed_main_tour && !flags.has_seen_circle_popup) {
          const timer = setTimeout(() => {
            setShowLoveLinkIntro(true);
          }, 2000); // 2 second delay after dashboard loads
          return () => clearTimeout(timer);
        }
      } catch (err) {
        // Fallback to user-scoped localStorage
        try {
          const circlePopupSeen = localStorage.getItem(`ul_circle_seen_${user.id}`);
          const tourCompleted = localStorage.getItem(`ul_tour_completed_${user.id}`) || localStorage.getItem('unitylove_onboarding_v1');
          
          if (tourCompleted && !circlePopupSeen) {
            const timer = setTimeout(() => {
              setShowLoveLinkIntro(true);
            }, 2000);
            return () => clearTimeout(timer);
          }
        } catch (e) {
          // ignore
        }
      }
    };
    
    checkAndShowLoveLinkIntro();
  }, [profile, uiFlagsLoaded, user?.id, token]);

  // Fetch LoveLink URL
  useEffect(() => {
    const fetchLoveLinkUrl = async () => {
      if (!token) return;
      try {
        const response = await apiClient.get(`/referrals/dashboard`);
        setLoveLinkUrl(response.data.referralLink || null);
      } catch (error) {
        console.warn('Failed to fetch LoveLink URL:', error);
      }
    };
    
    if (token) {
      fetchLoveLinkUrl();
    }
  }, [token]);

  const handleCloseLoveLinkIntro = async () => {
    // Persist to backend first (canonical source)
    if (token) {
      try {
        await apiClient.patch(`/me/ui-flags`, { has_seen_circle_popup: true });
      } catch (err) {
        console.warn('[Dashboard] Failed to persist circle popup seen to backend:', err);
      }
    }
    
    // Also set user-scoped localStorage as fallback
    try {
      if (user?.id) {
        localStorage.setItem(`ul_circle_seen_${user.id}`, '1');
      }
      // Keep legacy key for backward compatibility
      localStorage.setItem('lovelink_intro_seen', '1');
    } catch (e) {
      // ignore
    }
    setShowLoveLinkIntro(false);
  };

  // Derived values for avatar and display
  const displayName = useMemo(() => {
    return (
      profile?.display_name ||
      profile?.names?.[0] ||
      user?.name ||
      user?.first_name ||
      "there"
    );
  }, [profile, user]);

  // Use unified resolver for avatar - handles all field variants
  const mainPhotoUrl = useMemo(() => {
    // Prefer profile data (most complete), fallback to user object
    return resolveMainPhoto(profile) || resolveMainPhoto(user);
  }, [profile, user]);

  const handleViewProfile = async () => {
    // Canonical profile viewer: fetch from same endpoint used everywhere else
    // This removes the "Franken-profile" stitched from multiple sources
    try {
      if (!token || !user?.id) return;

      const res = await apiClient.get(`/users/profile/${user.id}`);

      profileViewer.showProfile(res.data, {
        isSelf: true,
        source: "dashboard:self",
      });
    } catch (e) {
      console.warn("[Dashboard] Failed to fetch self profile, using fallback:", e);
      // Fallback: if fetch fails, still show something rather than nothing
      const fallback = {
        display_name: user?.name || user?.first_name || "You",
        photos: [],
        profile_photos: [],
        bio: "",
        interests: [],
      };
      profileViewer.showProfile(fallback, { isSelf: true, source: "dashboard:fallback" });
    }
  };

  const handleEditProfile = () => {
    navigate("/edit-profile");
  };

  // Use shared utility for consistent completion calculation
  const profileCompletion = calculateProfileCompletion(profile);

  // Simple stub – can be replaced later with real stats from an API
  const dashboardStats = {
    flirts_received: flirts?.length ?? 0,
    likes_received: user?.stats?.likes_received ?? 0,
    matches: matches?.length ?? 0,
    remaining_lounge_minutes:
      user?.stats?.remaining_lounge_minutes ??
      (typeof loungeStatus?.remaining_seconds === "number"
        ? Math.floor(loungeStatus.remaining_seconds / 60)
        : null),
  };

  // ============================================================================
  // B3: DASHBOARD API CALLS - PARALLELIZE + DO NOT BLOCK UI
  // Render immediately with skeleton placeholders, fire API calls in parallel
  // ============================================================================
  useEffect(() => {
    const DEBUG_TIMING = process.env.REACT_APP_DEBUG_TIMING === '1';
    const logTiming = (label, ms) => {
      if (DEBUG_TIMING) {
        console.info(`[TIMING] ${label}: ${ms}ms`);
      }
    };
    
    const fetchData = async () => {
      const startTime = performance.now();
      
      // 🎯 FIX: Profile status state machine - prevents random teleports
      // Only redirect when profileStatus === 'missing' (true 404)
      // Never redirect when 'loading' or 'error'
      setProfileStatus('loading');

      // PERF: Do not block dashboard rendering on the /profiles/me check.
      // If profile is truly missing (404) we will redirect once confirmed.
      setLoading(false);
      logTiming('dashboard_first_paint_ms', Math.round(performance.now() - startTime));

      // ============================================================================
      // CRITICAL PATH: Profile check must complete first (may redirect)
      // ============================================================================
      let profileData = null;
      try {
        const profileResponse = await apiClient.get(`/profiles/me`);
        profileData = profileResponse.data;
        setProfile(profileData);
        setProfileStatus('exists'); // Profile confirmed to exist
      } catch (profileError) {
        if (profileError.response?.status === 404) {
          // Profile truly doesn't exist
          console.log('[Dashboard] Profile status: MISSING (true 404)');
          setProfileStatus('missing');
          navigate('/profile-setup');
          return; // Stop further fetching
        }
        // For other errors (network, 500, etc.), mark as error but DON'T redirect
        console.error('[Dashboard] Profile fetch error (non-404):', profileError);
        setProfileStatus('error');
        // Continue loading rest of dashboard - user has a profile, just couldn't fetch it
      }
      
      // ============================================================================
      // B3: PARALLEL FETCH - Fire all non-critical requests simultaneously
      // Dashboard renders with skeleton while these load
      // ============================================================================
      const parallelRequests = [
        // P1 profile for completion tracking
        apiClient.get(`/me/profile`)
          .then(res => ({ type: 'p1Profile', data: res.data.profile }))
          .catch(err => ({ type: 'p1Profile', error: err })),
        
        // Matches - normalize to fields the UI renders: match.name, match.photo, match.location
        apiClient.get(`/matches`)
          .then(res => {
            const data = normalizeList(res.data, ['matches']);
            const normalized = data.map((m) => {
              const p = m?.profile || {};
              const rawName =
                p.display_name ??
                p.name ??
                p.full_name ??
                p.first_name ??
                "";
              const cleanName =
                rawName && !["none", "null", "undefined"].includes(String(rawName).toLowerCase())
                  ? rawName
                  : "";
              const photo =
                p.primary_photo_url ??
                p.photo_url ??
                p.avatar_url ??
                "";
              const location =
                p.location ??
                (p.city && p.state ? `${p.city}, ${p.state}` : "") ??
                "";
              return {
                ...m,
                name: cleanName,
                photo,
                location,
              };
            });
            return { type: 'matches', data: normalized };
          })
          .catch(err => ({ type: 'matches', error: err })),
        
        // Flirts
        apiClient.get(`/flirts/received`)
          .then(res => ({ type: 'flirts', data: normalizeList(res.data, ['flirts']) }))
          .catch(err => ({ type: 'flirts', error: err })),
        
        // Badge counts
        apiClient.get(`/notifications/summary`)
          .then(res => ({ type: 'badges', data: res.data }))
          .catch(err => ({ type: 'badges', error: err })),
        
        // Subscription status
        // Subscription status
        apiClient.get(`/subscription/status`)
          .then(res => ({ type: 'subscription', data: res.data }))
          .catch(err => ({ type: 'subscription', error: err })),
        
        // Lounge status
        apiClient.get(`/lounge/status`)
          .then(res => ({ type: 'lounge', data: res.data }))
          .catch(err => ({ type: 'lounge', error: err })),
        
        // Daily flow status - Use unity-flow/status as canonical source
        apiClient.get(`/unity-flow/status`)
          .then(res => ({ type: 'dailyFlow', data: res.data }))
          .catch(err => ({ type: 'dailyFlow', error: err })),
      ];
      
      // B3: Fire all parallel requests (dashboard already visible)
      
      // Process parallel results as they complete
      const results = await Promise.allSettled(parallelRequests);
      
      results.forEach((result, index) => {
        if (result.status === 'fulfilled' && !result.value.error) {
          const { type, data } = result.value;
          switch (type) {
            case 'p1Profile':
              setP1Profile(data);
              break;
            case 'matches':
              setMatches(data);
              break;
            case 'flirts':
              setFlirts(data);
              break;
            case 'badges':
              setBadgeCounts({
                matches_unread: data.matches_unread || 0,
                likes_unread: data.likes_unread || 0,
                messages_unread: data.messages_unread || 0,
                flirts_unread: data.flirts_unread || 0,
              });
              break;
            case 'subscription':
              setSubscriptionStatus(data);
              break;
            case 'lounge':
              setLoungeStatus(data);
              break;
            case 'dailyFlow':
              setDailyFlow(data);
              break;
          }
        } else {
          // Log errors but don't crash
          const type = result.value?.type || `request_${index}`;
          console.log(`[Dashboard] Could not fetch ${type}:`, result.value?.error || result.reason);
        }
      });
      
      logTiming('dashboard_all_requests_ms', Math.round(performance.now() - startTime));
    };

    fetchData();
  }, [token, navigate]);

  // Refetch data when returning to Dashboard (fixes flirt check not updating)
  // Note: location is already declared at the top of the component
  const [dailyStreak, setDailyStreak] = useState(null);

  useEffect(() => {
    // Only refetch if we're actually on the dashboard route and have a token
    if (location.pathname === '/dashboard' && token && !loading) {
      const refetchData = async () => {
        try {
          // 🎯 Refetch Unity Flow daily task status (for green check update)
          // Use unity-flow/status as canonical source
          try {
            const dailyFlowResponse = await apiClient.get(`/unity-flow/status`);
            setDailyFlow(dailyFlowResponse.data);
            // Also update streak from unity-flow/status
            setDailyStreak({
              streak_days: dailyFlowResponse.data.streak_current || 0,
              tasks: {
                likes: { completed: !!dailyFlowResponse.data.tasks_done?.LIKES },
                flirt: { completed: !!dailyFlowResponse.data.tasks_done?.FLIRT_SENT },
                lounge: { completed: !!dailyFlowResponse.data.tasks_done?.LOUNGE_VISIT },
              }
            });
          } catch (err) {
            console.log('Refetch: Could not fetch daily flow status:', err);
          }

          // Skip legacy /unity-flow/streak - streak is now from unity-flow/status above

          // Refetch profile
          const profileResponse = await apiClient.get(`/profiles/me`);
          setProfile(profileResponse.data);

          // Refetch lounge status
          const loungeResponse = await apiClient.get(`/lounge/status`);
          setLoungeStatus(loungeResponse.data);
        } catch (err) {
          console.log('Refetch failed:', err);
        }
      };

      refetchData();
    }
  }, [location.key, token, loading]); // Runs when route key changes (navigation)
  
  // God Mode: Derive admin status from user object (no noisy API call)
  const isAdmin = !!(user?.is_admin || user?.roles?.includes('admin'));

  // 🎯 FIX: Refetch Unity Flow when user tabs back to the page
  // This ensures the green dot updates immediately when returning from completing a task
  useEffect(() => {
    const refreshUnityFlow = async () => {
      if (!token || loading) return;
      try {
        // Use unity-flow/status as canonical source
        const dailyFlowResponse = await apiClient.get(`/unity-flow/status`);
        setDailyFlow(dailyFlowResponse.data);
        console.log('[Dashboard] Unity Flow refreshed on focus');
      } catch (err) {
        console.log('[Dashboard] Could not refresh Unity Flow on focus:', err);
      }
    };

    // Expose refresh function globally for other components to call
    window.refreshUnityFlow = refreshUnityFlow;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshUnityFlow();
      }
    };

    const handleFocus = () => {
      refreshUnityFlow();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      delete window.refreshUnityFlow;
    };
  }, [token, loading]);

  // 🎯 Patch 7 Part 4: Poll unread message count
  useEffect(() => {
    if (!token) return;

    let cancelled = false;

    const fetchUnread = async () => {
      try {
        const res = await apiClient.get(`/chat/unread-count`);
        if (!cancelled) {
          setUnreadCount(res.data?.unread_count || 0);
        }
      } catch (err) {
        console.error('[dashboard] Failed to fetch unread count', err);
      }
    };

    fetchUnread();
    const intervalId = setInterval(fetchUnread, 30000); // Poll every 30s

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [token]);

  const handleLogout = () => {
    logout();
    navigate('/', { replace: true });
  };

  // ============================================================================
  // B3: DASHBOARD SKELETON - Shows immediately, sections load progressively
  // ============================================================================
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-warm">
        {/* Navigation Skeleton */}
        <nav className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm border-b border-rose-100 dark:border-slate-700 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-3 sm:py-4 h-14 sm:h-auto">
              <div className="flex items-center space-x-2">
                <Heart className="w-7 h-7 sm:w-8 sm:h-8 text-rose-600 fill-current" />
                <span className="font-playfair text-xl sm:text-2xl font-bold text-gray-900">
                  Unity Love
                </span>
              </div>
              <div className="flex items-center gap-2 sm:gap-4">
                <div className="w-8 h-8 bg-gray-200 dark:bg-slate-700 rounded animate-pulse"></div>
                <div className="w-8 h-8 bg-gray-200 dark:bg-slate-700 rounded animate-pulse"></div>
                <div className="w-8 h-8 bg-gray-200 dark:bg-slate-700 rounded animate-pulse"></div>
              </div>
            </div>
          </div>
        </nav>

        {/* Main Content Skeleton */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <div className="space-y-6">
            {/* Header Skeleton */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
              <div>
                <div className="h-8 bg-gray-200 dark:bg-slate-700 rounded w-48 animate-pulse mb-2"></div>
                <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-64 animate-pulse"></div>
              </div>
              <div className="flex gap-2">
                <div className="h-10 bg-gray-200 dark:bg-slate-700 rounded-full w-24 animate-pulse"></div>
                <div className="h-10 bg-gray-200 dark:bg-slate-700 rounded-full w-24 animate-pulse"></div>
              </div>
            </div>

            {/* Grid Skeleton */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Card Skeletons */}
              {[1, 2, 3].map((i) => (
                <div key={i} className="glass dark:bg-slate-900/60 rounded-2xl p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gray-200 dark:bg-slate-700 rounded-full animate-pulse"></div>
                    <div className="flex-1">
                      <div className="h-5 bg-gray-200 dark:bg-slate-700 rounded w-32 animate-pulse mb-2"></div>
                      <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-24 animate-pulse"></div>
                    </div>
                  </div>
                  <div className="h-20 bg-gray-200 dark:bg-slate-700 rounded animate-pulse"></div>
                </div>
              ))}
            </div>

            {/* Stats Row Skeleton */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="glass dark:bg-slate-900/60 rounded-xl p-4">
                  <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-16 animate-pulse mb-2"></div>
                  <div className="h-8 bg-gray-200 dark:bg-slate-700 rounded w-12 animate-pulse"></div>
                </div>
              ))}
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-warm" data-testid="dashboard-root">
      {/* Navigation - Mobile-first spacing */}
      <nav className="bg-white/90 dark:bg-slate-900/90 backdrop-blur-sm border-b border-rose-100 dark:border-slate-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3 sm:py-4 h-14 sm:h-auto">
            <div className="flex items-center space-x-1 sm:space-x-2">
              {/* Hamburger — mobile only */}
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="sm:hidden flex items-center justify-center w-10 h-10 -ml-1 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-rose-50 dark:hover:bg-slate-800 active:scale-95 transition"
                aria-label="Open menu"
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-6 h-6" />
                {(badgeCounts.matches_unread + badgeCounts.flirts_unread + badgeCounts.messages_unread) > 0 && (
                  <span className="absolute -mt-5 ml-5 bg-rose-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center leading-none">·</span>
                )}
              </button>
              <Heart className="w-7 h-7 sm:w-8 sm:h-8 text-rose-600 fill-current" />
              <span className="font-playfair text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                Unity Love
              </span>
            </div>
            
            {/* Icons row — horizontally scrollable on mobile, normal flex on desktop */}
            <div className="flex items-center gap-3 sm:gap-4 md:gap-6 overflow-x-auto no-scrollbar flex-1 sm:flex-initial justify-end pl-2 sm:pl-0">
            <style>{`.no-scrollbar::-webkit-scrollbar{display:none}.no-scrollbar{-ms-overflow-style:none;scrollbar-width:none}`}</style>
              <button
                onClick={() => navigate('/discover')}
                className="flex items-center space-x-2 text-gray-700 hover:text-rose-600 flex-shrink-0"
                data-testid="discover-nav-btn"
              >
                <Search className="w-5 h-5" />
                <span className="hidden sm:block">Discover</span>
              </button>

              <button
                onClick={() => navigate('/arcade')}
                className="flex items-center space-x-2 text-gray-700 hover:text-violet-600 relative"
                data-testid="arcade-nav-btn"
                title="Connection Arcade"
              >
                <Gamepad2 className="w-5 h-5" />
                <span className="hidden sm:block">Arcade</span>
                {arcadeBadge.hasBadge && (
                  <span className="absolute -top-1 -right-1 sm:relative sm:top-auto sm:right-auto sm:ml-1.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-violet-600 text-white text-[10px] font-bold leading-none">
                    {arcadeBadge.label}
                  </span>
                )}
              </button>
              
              <button
                onClick={() => navigate('/matches')}
                className="flex items-center space-x-2 text-gray-700 hover:text-rose-600 relative flex-shrink-0"
                data-testid="matches-nav-btn"
              >
                <MessageCircle className="w-5 h-5" />
                <span className="hidden sm:block">Matches</span>
                {/* Combined: matches + flirts unread */}
                {(badgeCounts.matches_unread + badgeCounts.flirts_unread) > 0 && (
                  <span className="absolute -top-2 -right-2 bg-rose-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {(badgeCounts.matches_unread + badgeCounts.flirts_unread) > 9 ? '9+' : badgeCounts.matches_unread + badgeCounts.flirts_unread}
                  </span>
                )}
              </button>

              <button
                onClick={() => navigate('/inbox')}
                className="flex items-center space-x-2 text-gray-700 hover:text-rose-600 relative flex-shrink-0"
                data-testid="inbox-nav-btn"
              >
                <Mail className="w-5 h-5" />
                <span className="hidden sm:block">Messages</span>
                {/* UNREAD only - disappears at 0 */}
                {badgeCounts.messages_unread > 0 && (
                  <span className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {badgeCounts.messages_unread > 9 ? '9+' : badgeCounts.messages_unread}
                  </span>
                )}
              </button>

              <button
                onClick={() => navigate('/wingman')}
                className="flex items-center space-x-2 text-gray-700 hover:text-rose-600 relative flex-shrink-0"
                data-testid="wingman-nav-btn"
              >
                <Sparkles className="w-5 h-5" />
                <span className="hidden sm:block">Wingman</span>
              </button>

              <button
                onClick={() => navigate('/shop')}
                className="flex items-center space-x-2 text-gray-700 hover:text-pink-600 flex-shrink-0"
                data-testid="shop-nav-btn"
              >
                <ShoppingCart className="w-5 h-5" />
                <span className="hidden sm:block">Shop</span>
              </button>

              <button
                onClick={() => navigate('/connections')}
                className="flex items-center space-x-2 text-gray-700 hover:text-indigo-600 flex-shrink-0"
                data-testid="connections-nav-btn"
                title="Connections - multi-mode networking"
              >
                <Users className="w-5 h-5" />
                <span className="hidden sm:block">Connect</span>
              </button>

              <button
                onClick={() => navigate('/elite')}
                className="flex items-center space-x-2 text-gray-700 hover:text-yellow-600"
                data-testid="elite-nav-btn"
              >
                <Crown className="w-5 h-5" />
                <span className="hidden sm:block">Elite</span>
              </button>

              {/* 7️⃣ Meditation - secondary access, de-emphasized, no label, no badge */}
              <button
                onClick={() => navigate('/meditation')}
                className="hidden sm:flex items-center justify-center text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400 transition-colors"
                title="Meditation"
                aria-label="Meditation"
                data-testid="meditation-header-btn"
              >
                <Waves className="w-5 h-5" />
              </button>

              {/* Theme Toggle */}
              <button
                onClick={() => {
                  const root = document.documentElement;
                  const isDark = root.classList.contains('dark');
                  if (isDark) {
                    root.classList.remove('dark');
                    root.style.colorScheme = 'light';
                    localStorage.setItem('unitylove_theme', 'light');
                  } else {
                    root.classList.add('dark');
                    root.style.colorScheme = 'dark';
                    localStorage.setItem('unitylove_theme', 'dark');
                  }
                  // Force re-render
                  window.dispatchEvent(new Event('themechange'));
                }}
                className="hidden sm:flex items-center justify-center text-gray-700 hover:text-rose-600 dark:text-amber-400 dark:hover:text-amber-300 transition-colors flex-shrink-0"
                title="Toggle theme"
                aria-label="Toggle light/dark theme"
                data-testid="theme-toggle-btn"
              >
                <Moon className="w-5 h-5 dark:hidden" />
                <Sun className="w-5 h-5 hidden dark:block" />
              </button>

              {/* Settings cog dropdown — desktop only (hidden on mobile since
                  everything is in the hamburger drawer). */}
              <div className="hidden sm:block relative group flex-shrink-0">
                <button className="flex items-center space-x-2 text-gray-700 hover:text-rose-600 dark:text-gray-300 dark:hover:text-rose-400">
                  <Settings className="w-5 h-5" />
                </button>
                
                <div className="absolute right-0 mt-2 w-52 bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                  {/* Profile (View Only) */}
                  <button
                    onClick={handleViewProfile}
                    className="block px-4 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700 w-full text-left"
                  >
                    <Eye className="w-4 h-4 inline mr-2" />
                    Profile
                  </button>
                  
                  {/* Edit Profile */}
                  <button
                    onClick={() => navigate('/edit-profile')}
                    className="block px-4 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700 w-full text-left"
                  >
                    <Edit className="w-4 h-4 inline mr-2" />
                    Edit Profile
                  </button>
                  
                  {/* Profile Builder - always visible (guided wizard mode of edit-profile) */}
                  <button
                    onClick={() => navigate('/edit-profile#setup')}
                    className="block px-4 py-2 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 w-full text-left"
                    data-testid="nav-profile-builder"
                  >
                    <Sparkles className="w-4 h-4 inline mr-2" />
                    Profile Builder
                    <span className="text-xs text-amber-500 dark:text-amber-400/70 block mt-0.5">{profileCompletion}% complete</span>
                  </button>
                  
                  <div className="border-t border-gray-200 dark:border-gray-700 my-2"></div>
                  
                  {/* Love Links */}
                  <button
                    onClick={() => navigate('/lovelinks/dashboard')}
                    className="block px-4 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700 w-full text-left"
                  >
                    <DollarSign className="w-4 h-4 inline mr-2 text-emerald-500" />
                    Love Links
                    <span className="text-xs text-gray-400 dark:text-gray-500 block mt-0.5">Your network & rewards</span>
                  </button>
                  
                  {/* Inbox */}
                  <button
                    onClick={() => navigate('/inbox')}
                    className="block px-4 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700 w-full text-left relative"
                  >
                    <Mail className="w-4 h-4 inline mr-2 text-blue-500" />
                    Inbox
                    {unreadCount > 0 && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 bg-pink-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                        {unreadCount}
                      </span>
                    )}
                  </button>
                  
                  <div className="border-t border-gray-200 dark:border-gray-700 my-2"></div>
                  
                  <button
                    onClick={() => navigate('/discovery-preferences')}
                    className="block px-4 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700 w-full text-left"
                  >
                    <Search className="w-4 h-4 inline mr-2" />
                    Discovery Preferences
                  </button>
                  
                  <button
                    onClick={() => navigate('/connections')}
                    className="block px-4 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700 w-full text-left"
                  >
                    <Users className="w-4 h-4 inline mr-2 text-indigo-500" />
                    Connections
                  </button>

                  {/* Daily Mirror */}
                  <button
                    onClick={() => navigate('/daily-mirror')}
                    className="block px-4 py-2 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 w-full text-left"
                  >
                    <Camera className="w-4 h-4 inline mr-2" />
                    Daily Mirror
                    <span className="text-xs text-gray-400 dark:text-gray-500 block mt-0.5">
                      Affirmations + reflection
                    </span>
                  </button>

                  <div className="border-t border-gray-200 dark:border-gray-700 my-2"></div>

                  <button
                    onClick={() => navigate('/education/love-your-way')}
                    className="block px-4 py-2 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 w-full text-left"
                  >
                    <BookOpen className="w-4 h-4 inline mr-2" />
                    Love Your Way Guide
                  </button>
                  <button
                    onClick={() => navigate('/help')}
                    className="block px-4 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700 w-full text-left"
                  >
                    <HelpCircle className="w-4 h-4 inline mr-2" />
                    Help Center
                  </button>

                  <button
                    onClick={() => navigate('/settings')}
                    className="block px-4 py-2 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-700 w-full text-left"
                    data-testid="settings-nav-btn"
                  >
                    <Settings className="w-4 h-4 inline mr-2" />
                    Settings
                  </button>
                  
                  {/* Meditation - de-emphasized, in dropdown for minimal presence */}
                  <button
                    onClick={() => navigate('/meditation')}
                    className="block px-4 py-2 text-gray-400 dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-slate-700 hover:text-gray-600 dark:hover:text-gray-400 w-full text-left"
                    data-testid="meditation-nav-btn"
                  >
                    <Waves className="w-4 h-4 inline mr-2" />
                    Meditation
                  </button>
                  
                  {isAdmin && (
                    <button
                      onClick={() => navigate('/admin')}
                      className="block px-4 py-2 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 w-full text-left border-t border-gray-200 dark:border-gray-700 font-medium"
                      data-testid="admin-nav-btn"
                    >
                      <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-400 mr-2" />
                      Admin Dashboard
                    </button>
                  )}
                  
                  <button
                    onClick={handleLogout}
                    className="block px-4 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 w-full text-left border-t border-gray-200 dark:border-gray-700"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4 inline mr-2" />
                    Sign Out
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* ===== MOBILE DRAWER ===== */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-[100] sm:hidden"
          data-testid="mobile-menu-drawer"
          aria-modal="true"
          role="dialog"
        >
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          />
          {/* Panel */}
          <div className="absolute inset-y-0 left-0 w-72 max-w-[85%] bg-white dark:bg-slate-900 shadow-2xl flex flex-col overflow-y-auto animate-in slide-in-from-left duration-200">
            <div className="flex items-center justify-between px-4 py-4 border-b border-gray-100 dark:border-slate-700">
              <div className="flex items-center gap-2">
                <Heart className="w-6 h-6 text-rose-600 fill-current" />
                <span className="font-playfair text-lg font-bold text-gray-900 dark:text-white">Unity Love</span>
              </div>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-center w-9 h-9 rounded-full text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-800 active:scale-95 transition"
                aria-label="Close menu"
                data-testid="mobile-menu-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <nav className="flex-1 py-2">
              <MobileNavItem icon={<Search className="w-5 h-5" />} label="Discover" onClick={() => mobileGo('/discover')} testId="m-nav-discover" />
              <MobileNavItem
                icon={<Gamepad2 className="w-5 h-5" />} label="Arcade"
                badge={arcadeBadge.hasBadge ? arcadeBadge.label : null} badgeColor="violet"
                onClick={() => mobileGo('/arcade')} testId="m-nav-arcade"
              />
              <MobileNavItem
                icon={<MessageCircle className="w-5 h-5" />} label="Matches"
                badge={(badgeCounts.matches_unread + badgeCounts.flirts_unread) > 0 ? ((badgeCounts.matches_unread + badgeCounts.flirts_unread) > 9 ? '9+' : (badgeCounts.matches_unread + badgeCounts.flirts_unread)) : null}
                badgeColor="rose"
                onClick={() => mobileGo('/matches')} testId="m-nav-matches"
              />
              <MobileNavItem
                icon={<Mail className="w-5 h-5" />} label="Messages"
                badge={badgeCounts.messages_unread > 0 ? (badgeCounts.messages_unread > 9 ? '9+' : badgeCounts.messages_unread) : null}
                badgeColor="blue"
                onClick={() => mobileGo('/inbox')} testId="m-nav-inbox"
              />
              <MobileNavItem icon={<Sparkles className="w-5 h-5" />} label="Wingman" onClick={() => mobileGo('/wingman')} testId="m-nav-wingman" />
              <MobileNavItem icon={<ShoppingCart className="w-5 h-5" />} label="Shop" onClick={() => mobileGo('/shop')} testId="m-nav-shop" />
              <MobileNavItem icon={<Users className="w-5 h-5" />} label="Connect" onClick={() => mobileGo('/connections')} testId="m-nav-connect" />
              <MobileNavItem icon={<Crown className="w-5 h-5" />} label="Elite" onClick={() => mobileGo('/elite')} testId="m-nav-elite" />
              <MobileNavItem icon={<Heart className="w-5 h-5" />} label="Daily Mirror" onClick={() => mobileGo('/daily-mirror')} testId="m-nav-daily-mirror" />
              <MobileNavItem icon={<Sparkles className="w-5 h-5" />} label="Love Links" onClick={() => mobileGo('/lovelink')} testId="m-nav-love-links" />

              <div className="border-t border-gray-100 dark:border-slate-700 my-2"></div>

              <MobileNavItem icon={<Eye className="w-5 h-5" />} label="View Profile" onClick={() => { setMobileMenuOpen(false); handleViewProfile(); }} testId="m-nav-profile" />
              <MobileNavItem icon={<Edit className="w-5 h-5" />} label="Edit Profile" onClick={() => mobileGo('/edit-profile')} testId="m-nav-edit-profile" />
              <MobileNavItem
                icon={<Sparkles className="w-5 h-5" />}
                label={`Profile Builder · ${profileCompletion}%`}
                amber
                onClick={() => mobileGo('/edit-profile#setup')}
                testId="m-nav-profile-builder"
              />
              <MobileNavItem icon={<Search className="w-5 h-5" />} label="Discovery Preferences" onClick={() => mobileGo('/discovery-preferences')} testId="m-nav-discovery-prefs" />
              <MobileNavItem icon={<BookOpen className="w-5 h-5" />} label="Love Your Way" onClick={() => mobileGo('/education/love-your-way')} testId="m-nav-loveyourway" />
              <MobileNavItem icon={<HelpCircle className="w-5 h-5" />} label="Help Center" onClick={() => mobileGo('/help')} testId="m-nav-help" />
              <MobileNavItem icon={<Settings className="w-5 h-5" />} label="Settings" onClick={() => mobileGo('/settings')} testId="m-nav-settings" />
              <MobileNavItem icon={<Waves className="w-5 h-5" />} label="Meditation" muted onClick={() => mobileGo('/meditation')} testId="m-nav-meditation" />

              {/* Theme toggle row */}
              <button
                onClick={() => {
                  const root = document.documentElement;
                  const isDark = root.classList.contains('dark');
                  if (isDark) {
                    root.classList.remove('dark');
                    root.style.colorScheme = 'light';
                    localStorage.setItem('unitylove_theme', 'light');
                  } else {
                    root.classList.add('dark');
                    root.style.colorScheme = 'dark';
                    localStorage.setItem('unitylove_theme', 'dark');
                  }
                  window.dispatchEvent(new Event('themechange'));
                }}
                className="flex items-center gap-3 w-full px-4 py-3 text-gray-700 dark:text-gray-200 hover:bg-rose-50 dark:hover:bg-slate-800 transition"
                data-testid="m-nav-theme"
              >
                <Moon className="w-5 h-5 dark:hidden" />
                <Sun className="w-5 h-5 hidden dark:block text-amber-400" />
                <span className="text-sm font-medium">Toggle Theme</span>
              </button>

              {isAdmin && (
                <>
                  <div className="border-t border-gray-100 dark:border-slate-700 my-2"></div>
                  <MobileNavItem icon={<span className="inline-block h-2 w-2 rounded-full bg-amber-400" />} label="Admin Dashboard" amber onClick={() => mobileGo('/admin')} testId="m-nav-admin" />
                </>
              )}

              <div className="border-t border-gray-100 dark:border-slate-700 my-2"></div>

              <button
                onClick={() => { setMobileMenuOpen(false); handleLogout(); }}
                className="flex items-center gap-3 w-full px-4 py-3 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition"
                data-testid="m-nav-logout"
              >
                <LogOut className="w-5 h-5" />
                <span className="text-sm font-medium">Sign Out</span>
              </button>
            </nav>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header with avatar */}
        <header className="dashboard-header">
          <div className="dashboard-header-left">
            {mainPhotoUrl ? (
              <img
                src={mainPhotoUrl}
                alt={displayName}
                className="dashboard-avatar"
                referrerPolicy="no-referrer"
                onError={(e) => {
                  // External avatars (e.g., DiceBear) can be blocked by ad-blockers / private DNS.
                  // Replace broken img with the local initials fallback.
                  const fallback = document.createElement('div');
                  fallback.className = 'dashboard-avatar dashboard-avatar-fallback';
                  fallback.textContent = (displayName?.charAt(0) || '?').toUpperCase();
                  e.currentTarget.parentNode.replaceChild(fallback, e.currentTarget);
                }}
              />
            ) : (
              <div className="dashboard-avatar dashboard-avatar-fallback">
                {displayName?.charAt(0).toUpperCase()}
              </div>
            )}

            <div>
              <h1 className="dashboard-title">
                Welcome back, {displayName}!
              </h1>
              <p className="dashboard-subtitle">
                Ready to make new connections?
              </p>
            </div>
          </div>
        </header>

        {/* Today's Unity Flow - Daily engagement tracker */}
        <TodayFlow dailyFlow={dailyFlow} />

        {/* Next Milestone Card - Unity Flow milestone progress */}
        {dailyFlow && (
          <div className="mb-6">
            <RetentionNextMilestoneCard
              streakCurrent={dailyFlow.streak_current || 0}
              nextMilestoneDay={dailyFlow.next_milestone_day}
              nextMilestoneRewardMinutes={dailyFlow.next_milestone_reward_minutes || 0}
              daysRemaining={dailyFlow.days_remaining_to_next_milestone || 0}
              nextClaimableMilestone={dailyFlow.next_claimable_milestone}
              claimableBonusMinutes={dailyFlow.claimable_bonus_minutes || 0}
            />
          </div>
        )}

        {/* Community Theme Badge */}
        {hasRhythmTheme && (
          <div className="mb-6">
            <RoomPromptBanner
              theme={rhythmTheme}
              tagline={rhythmTagline}
              showTheme={true}
              compact={true}
            />
          </div>
        )}

        {/* PATCH B: Private Recognition Badges */}
        <div className="mb-6">
          <RecognitionBadgeCard showProgress={true} />
        </div>

        {/* Inner Work - Reflection & Journaling */}
        <div className="mb-6">
          <InnerWorkCard />
        </div>

        {/* Rooms - Community Spaces */}
        <div className="mb-6">
          <button
            className="w-full rounded-2xl border border-gray-200 dark:border-slate-700/60 bg-white dark:bg-[#141b2d] p-5 text-left shadow-sm hover:bg-gray-50 dark:hover:bg-[#1a2236] hover:border-gray-300 dark:hover:border-slate-600 transition"
            onClick={() => navigate("/rooms")}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-100 to-pink-100 dark:from-rose-900/40 dark:to-pink-900/40 flex items-center justify-center">
                <Users className="w-5 h-5 text-rose-500 dark:text-rose-400" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold text-gray-900 dark:text-slate-100">Rooms</div>
                <div className="text-sm text-gray-500 dark:text-slate-400">Join calm community spaces for real conversation</div>
              </div>
              <span className="text-xs text-rose-500 dark:text-rose-400 font-medium px-2 py-1 bg-rose-50 dark:bg-rose-900/30 rounded-full">New</span>
            </div>
          </button>
        </div>

        {/* Stripe: Elite Upgrade CTA */}
        {!isPremium() && (
          <div className="mb-6 rounded-2xl bg-gradient-to-r from-rose-500 via-pink-500 to-purple-600 p-6 shadow-xl">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Crown className="text-yellow-300" size={24} />
                  <h3 className="text-xl font-bold text-white">
                    Unlock Elite Features
                  </h3>
                </div>
                <p className="text-white/90 mb-1">
                  See who liked you, get more likes, and stand out with an Elite badge
                </p>
                <p className="text-white/70 text-sm">
                  Join the exclusive Elite community
                </p>
              </div>
              <div className="ml-6">
                <UpgradeToEliteButton />
              </div>
            </div>
          </div>
        )}

        {/* P1: Profile Completion Banner */}
        {p1Completion < 70 && (
          <div className="mb-6 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white mb-1">
                  {getCompletionMessage(p1Completion)}
                </h3>
                <p className="text-purple-100 text-sm">
                  Add a few details to help others discover you
                </p>
                <div className="mt-3 w-full bg-purple-800/50 rounded-full h-2">
                  <div 
                    className="bg-white dark:bg-emerald-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${p1Completion}%` }}
                  />
                </div>
              </div>
              <button
                onClick={() => navigate('/edit-profile')}
                className="ml-6 rounded-lg bg-white dark:bg-slate-800 px-6 py-3 text-purple-600 dark:text-purple-400 font-semibold hover:bg-purple-50 dark:hover:bg-slate-700 transition whitespace-nowrap"
              >
                Complete Profile
              </button>
            </div>
          </div>
        )}

        {/* Membership banner */}
        {subscriptionStatus?.tier === 'premium' && (
          <div className="membership-banner">
            <div className="membership-banner-left">
              <span className="membership-crown">👑</span>
              <div>
                <div className="membership-title">
                  Unity {subscriptionStatus.tier === "elite" ? "Elite" : "Premium"} Member
                </div>
                {user?.subscription_expires_at && (
                  <div className="membership-subtitle">
                    Renews on{" "}
                    {new Date(user.subscription_expires_at).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>
            <button
              type="button"
              className="link-button"
              onClick={() => navigate("/premium")}
            >
              Manage plan
            </button>
          </div>
        )}

        {/* Your Profile card – clickable */}
        <section
          className="your-profile-card"
          role="button"
          onClick={handleViewProfile}
        >
          <div className="your-profile-header-row">
            <h2>Your Profile</h2>
            <div className="flex items-center gap-3">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewProfile();
                }}
                className="text-xs font-medium text-rose-600 hover:text-rose-700"
              >
                View profile →
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigate("/edit-profile#main-photo");
                }}
                className="text-xs font-medium text-sky-600 hover:text-sky-700"
              >
                {mainPhotoUrl ? "Change main photo" : "Add main photo"} →
              </button>
            </div>
          </div>

          <div className="your-profile-summary">
            <div className="your-profile-row">
              <div className="your-profile-label">Type</div>
              <div className="your-profile-value">
                {user?.user_type || "Profile"}
              </div>
            </div>
            <div className="your-profile-row">
              <div className="your-profile-label">Names</div>
              <div className="your-profile-value">
                {profile?.names?.join(' & ') || displayName}
              </div>
            </div>
            <div className="your-profile-row">
              <div className="your-profile-label">Location</div>
              <div className="your-profile-value">
                {profile?.location || user?.location || "Add your location"}
              </div>
            </div>
            <div className="your-profile-row">
              <div className="your-profile-label">Background</div>
              <div className="your-profile-value">
                {profile?.background || "Add your background"}
              </div>
            </div>
            {Array.isArray(profile?.interests) && profile.interests.length > 0 && (
              <div className="your-profile-row">
                <div className="your-profile-label">Interests</div>
                <div className="your-profile-chips">
                  {profile.interests.slice(0, 4).map((interest) => (
                    <span key={interest} className="chip">
                      {interest}
                    </span>
                  ))}
                  {profile.interests.length > 4 && (
                    <span className="chip chip-muted">
                      +{profile.interests.length - 4} more
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Profile completion bar */}
          <div
            className="profile-completion"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="profile-completion-top">
              <span>Profile completeness</span>
              <span>{profileCompletion}%</span>
            </div>
            <div className="profile-completion-bar">
              <div
                className="profile-completion-bar-fill"
                style={{ width: `${profileCompletion}%` }}
              />
            </div>
            {profileCompletion < 100 && (
              <button
                type="button"
                className="profile-completion-cta"
                onClick={handleEditProfile}
              >
                Finish your profile to get more matches →
              </button>
            )}
          </div>
        </section>

        {/* V2 Photo Nudge - Only show if no avatar */}
        {!mainPhotoUrl && (
          <div className="mt-4 mb-6 rounded-xl bg-gradient-to-r from-sky-500/10 to-purple-500/10 border border-sky-500/20 px-4 py-3">
            <p className="text-sm text-slate-300">
              💡 <strong>Quick tip:</strong> Add a clear main photo to stand out in Quick Match and get more responses.
            </p>
          </div>
        )}

        {/* Dashboard stats row */}
        <section className="dashboard-stats">
          <div className="stat">
            <span className="stat-label">Flirts received</span>
            <span className="stat-value">{dashboardStats.flirts_received}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Likes received</span>
            <span className="stat-value">{dashboardStats.likes_received}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Matches</span>
            <span className="stat-value">{dashboardStats.matches}</span>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="stat cursor-help" tabIndex={0} aria-label="Lounge time today - time you can spend in Lounge today">
                  <span className="stat-label flex items-center gap-1">
                    Lounge time
                    <Info className="w-3 h-3 text-gray-400" />
                  </span>
                  <span className="stat-value">
                    {dashboardStats.remaining_lounge_minutes != null
                      ? `${dashboardStats.remaining_lounge_minutes} min`
                      : "—"}
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" className="bg-slate-800 text-white border-slate-700 max-w-[200px]">
                <p className="text-xs">LoveLink Minutes: time you can spend in Lounge today.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </section>

        <div className="grid lg:grid-cols-1 gap-8">
          {/* Main Actions */}
          <div className="lg:col-span-1">
            <div className="grid gap-6">
              {/* Premium Status Banner */}
              {subscriptionStatus?.tier === 'premium' && (
                <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-2xl p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Crown className="w-6 h-6 text-yellow-500" />
                      <div>
                        <div className="font-semibold text-gray-900">Unity Elite Member</div>
                        <div className="text-sm text-gray-600">
                          Expires {new Date(subscriptionStatus.expires).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => navigate('/premium')}
                      className="text-yellow-600 hover:text-yellow-700 font-medium text-sm"
                    >
                      Elite Features →
                    </button>
                  </div>
                </div>
              )}

              {/* Today / This Week Events Section */}
              <section className="mb-6">
                <TodaysEventsCard />
              </section>

              {/* Quick Actions */}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                <div 
                  className="glass rounded-2xl p-8 card-hover cursor-pointer group"
                  onClick={() => navigate('/quick-match')}
                  data-testid="quick-match-card"
                >
                  <div className="bg-rose-100 w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:bg-rose-200 transition-colors">
                    <Search className="w-8 h-8 text-rose-600" />
                  </div>
                  <h3 className="font-playfair text-xl font-semibold mb-2">
                    Quick Match
                  </h3>
                  <p className="text-gray-600">
                    10-second decisions to find your perfect match
                  </p>
                </div>

                <div 
                  className="glass rounded-2xl p-8 card-hover cursor-pointer group"
                  onClick={() => navigate('/flirts')}
                  data-testid="flirts-card"
                >
                  <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:bg-purple-200 transition-colors relative">
                    <Sparkles className="w-8 h-8 text-purple-600" />
                    {/* Bubble = UNREAD only (disappears at 0) */}
                    {badgeCounts.flirts_unread > 0 && (
                      <span className="absolute -top-1 -right-1 bg-purple-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center">
                        {badgeCounts.flirts_unread}
                      </span>
                    )}
                  </div>
                  <h3 className="font-playfair text-xl font-semibold mb-2">
                    Flirt Center
                  </h3>
                  <p className="text-gray-600">
                    {/* Text = TOTALS (always visible) */}
                    {flirts.length > 0 
                      ? `You have ${flirts.length} flirt${flirts.length > 1 ? 's' : ''}`
                      : 'No flirts yet - send some to get noticed!'
                    }
                  </p>
                </div>

                <div 
                  className="glass rounded-2xl p-8 card-hover cursor-pointer group"
                  onClick={() => navigate('/matches')}
                  data-testid="matches-card"
                >
                  <div className="bg-rose-100 w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:bg-rose-200 transition-colors relative">
                    <MessageCircle className="w-8 h-8 text-rose-600" />
                    {/* Bubble = UNREAD only (disappears at 0) */}
                    {badgeCounts.matches_unread > 0 && (
                      <span className="absolute -top-1 -right-1 bg-rose-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center">
                        {badgeCounts.matches_unread}
                      </span>
                    )}
                  </div>
                  <h3 className="font-playfair text-xl font-semibold mb-2">
                    Your Matches
                  </h3>
                  <p className="text-gray-600">
                    {/* Text = TOTALS (always visible) */}
                    {matches.length > 0 
                      ? `You have ${matches.length} match${matches.length > 1 ? 'es' : ''}`
                      : 'No matches yet - start quick matching!'
                    }
                  </p>
                </div>

                {/* New Feature: Lifestyle Lounge */}
                <div 
                  className="glass rounded-2xl p-8 card-hover cursor-pointer group border border-white/20 hover:border-purple-400/50 bg-white/80 dark:bg-slate-900/60"
                  onClick={() => navigate('/lifestyle-lounge')}
                  data-testid="lifestyle-lounge-card"
                >
                  <div className="bg-purple-100 dark:bg-purple-900/40 w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:bg-purple-200 dark:group-hover:bg-purple-800/50 transition-colors relative">
                    <Users className="w-8 h-8 text-purple-600 dark:text-purple-400" />
                    <span className="absolute -top-1 -right-1 bg-gradient-to-r from-purple-500 to-rose-500 text-white text-xs rounded-full px-2 py-0.5">
                      NEW
                    </span>
                  </div>
                  <h3 className="font-playfair text-xl font-semibold mb-2 flex items-center space-x-2">
                    <span>The Lifestyle Lounge</span>
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    Join our virtual hangout space! Video, audio & text chat with the community
                  </p>
                  <p className="text-sm text-purple-600 dark:text-purple-400 font-semibold mt-2">
                    20 minutes free daily
                  </p>
                </div>

                {/* 🌌 Constellation System - M4: Universe Screen */}
                <div 
                  className="glass rounded-2xl p-8 card-hover cursor-pointer group border border-white/20 hover:border-indigo-400/50 bg-white/80 dark:bg-slate-900/60"
                  onClick={() => navigate('/universe')}
                  data-testid="universe-card"
                >
                  <div className="bg-indigo-100 dark:bg-indigo-900/40 w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:bg-indigo-200 dark:group-hover:bg-indigo-800/50 transition-colors relative">
                    <span className="text-3xl">🌌</span>
                    <span className="absolute -top-1 -right-1 bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-xs rounded-full px-2 py-0.5">
                      NEW
                    </span>
                  </div>
                  <h3 className="font-playfair text-xl font-semibold mb-2">
                    My Universe
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    Visualize your constellation of connections and see your network of love
                  </p>
                  <p className="text-sm text-indigo-600 dark:text-indigo-400 font-semibold mt-2">
                    Coming to life
                  </p>
                </div>

                {/* Ticket 28: Gift Leaderboards */}
                <div 
                  className="glass rounded-2xl p-8 card-hover cursor-pointer group border border-white/20 hover:border-pink-400/50 bg-white/80 dark:bg-slate-900/60"
                  onClick={() => navigate('/leaderboards/gifts')}
                  data-testid="gift-leaderboards-card"
                >
                  <div className="bg-pink-100 dark:bg-pink-900/40 w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:bg-pink-200 dark:group-hover:bg-pink-800/50 transition-colors relative">
                    <span className="text-3xl">🏆</span>
                  </div>
                  <h3 className="font-playfair text-xl font-semibold mb-2">
                    Gift Leaderboards
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    See who&apos;s spreading the love! Check weekly &amp; all-time top gifters
                  </p>
                </div>

                {/* Ticket 29: Elite Perks - PREMIUM CARD (keeps gradient) */}
                <div 
                  className="glass rounded-2xl p-8 card-hover cursor-pointer group border border-amber-400/50 bg-gradient-to-br from-amber-500/10 via-purple-500/10 to-rose-500/10 hover:border-amber-400"
                  onClick={() => navigate('/elite')}
                  data-testid="elite-perks-card"
                >
                  <div className="bg-gradient-to-br from-amber-100 to-purple-100 dark:from-amber-900/50 dark:to-purple-900/50 w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:from-amber-200 group-hover:to-purple-200 transition-colors relative">
                    <Crown className="w-8 h-8 text-amber-600 dark:text-amber-400" />
                  </div>
                  <h3 className="font-playfair text-xl font-semibold mb-2 flex items-center gap-2">
                    <span>Elite Perks</span>
                    <span className="text-xs bg-gradient-to-r from-amber-400 to-purple-600 text-white px-2 py-0.5 rounded-full">Premium</span>
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    Monthly coins, exclusive gifts & extended lounge time
                  </p>
                </div>

                {/* Ticket 32: Age Verification */}
                {!user?.is_age_verified && (
                  <div 
                    className="glass rounded-2xl p-8 card-hover cursor-pointer group border border-white/20 hover:border-emerald-400/50 bg-white/80 dark:bg-slate-900/60"
                    onClick={() => goVerify(navigate, location, null, { source: 'dashboard_verify_card', isLoggedIn: !!user })}
                    data-testid="age-verification-card"
                  >
                    <div className="bg-emerald-100 dark:bg-emerald-900/40 w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:bg-emerald-200 dark:group-hover:bg-emerald-800/50 transition-colors relative">
                      <span className="text-3xl">🛡️</span>
                    </div>
                    <h3 className="font-playfair text-xl font-semibold mb-2">
                      Verify Your Age
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Unlock After Dark & Speed Dating with secure ID verification
                    </p>
                  </div>
                )}

                {/* Speed Dating Hub - Single Entry Point */}
                <div 
                  className="glass rounded-2xl p-8 card-hover cursor-pointer group border border-white/20 hover:border-purple-400/50 bg-gradient-to-br from-purple-500/5 via-pink-500/5 to-transparent"
                  onClick={() => navigate('/speed-dating')}
                  data-testid="speed-dating-hub-card"
                >
                  <div className="bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/40 dark:to-pink-900/40 w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:from-purple-200 group-hover:to-pink-200 dark:group-hover:from-purple-800/50 dark:group-hover:to-pink-800/50 transition-colors relative">
                    <span className="text-3xl">⚡</span>
                    <span className="absolute -bottom-1 -right-1 text-lg">💬</span>
                  </div>
                  <h3 className="font-playfair text-xl font-semibold mb-2">
                    Speed Dating
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    Text or Video • Choose your vibe • Connect your way
                  </p>
                </div>

                {/* Premium Feature: Who Liked Me - Velvet Rope */}
                <div 
                  className={`glass rounded-2xl p-8 card-hover cursor-pointer group border ${
                    subscriptionStatus?.tier === 'premium' 
                      ? 'border-yellow-400/50 bg-gradient-to-br from-yellow-500/10 via-amber-500/10 to-orange-500/10 hover:border-yellow-400' 
                      : 'border-white/20 hover:border-gray-400/50 bg-white/80 dark:bg-slate-900/60'
                  }`}
                  onClick={() => navigate('/who-liked-me')}
                  data-testid="entry-who-liked-me"
                >
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-6 transition-colors relative ${
                    subscriptionStatus?.tier === 'premium' 
                      ? 'bg-yellow-100 dark:bg-yellow-900/40 group-hover:bg-yellow-200 dark:group-hover:bg-yellow-800/50' 
                      : 'bg-gray-100 dark:bg-gray-800/40 group-hover:bg-gray-200 dark:group-hover:bg-gray-700/50'
                  }`}>
                    <Eye className={`w-8 h-8 ${subscriptionStatus?.tier === 'premium' ? 'text-yellow-600 dark:text-yellow-400' : 'text-gray-500 dark:text-gray-400'}`} />
                    <Crown className={`w-4 h-4 absolute -top-1 -right-1 ${
                      subscriptionStatus?.tier === 'premium' ? 'text-yellow-500' : 'text-gray-400'
                    }`} />
                  </div>
                  <h3 className="font-playfair text-xl font-semibold mb-2">
                    Who Liked You
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {subscriptionStatus?.tier === 'premium' 
                      ? 'See who\'s interested in you - exclusive Elite feature'
                      : 'Upgrade to Elite to see who liked you'}
                  </p>
                  {subscriptionStatus?.tier !== 'premium' && (
                    <span className="inline-flex items-center mt-3 text-sm text-amber-600 font-medium">
                      <Lock className="w-4 h-4 mr-1" />
                      Elite Feature
                    </span>
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* Recent Matches - Between features and Wingman */}
        <section className="mt-8">
          <div className="glass rounded-2xl p-6">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Recent Matches
                </h3>
                <p className="text-xs text-slate-600">
                  People you&apos;ve matched with recently – reach out and keep the energy going.
                </p>
              </div>
              {matches && matches.length > 3 && (
                <button
                  type="button"
                  onClick={() => navigate('/matches')}
                  className="text-xs font-medium text-rose-600 hover:text-rose-700"
                >
                  View all ({matches.length})
                </button>
              )}
            </div>

            {matches && matches.length > 0 ? (
              <div className="space-y-2">
                {matches.slice(0, 3).map((match, index) => (
                  <button
                    key={match.id || index}
                    type="button"
                    onClick={() => navigate('/matches')}
                    className="w-full flex items-center justify-between rounded-xl px-3 py-2 bg-white dark:bg-slate-800 border border-rose-100 dark:border-slate-600 hover:border-rose-200 dark:hover:border-slate-500 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-rose-400 to-fuchsia-500 overflow-hidden flex-shrink-0">
                        {match.photo_url || match.main_photo_url ? (
                          <img
                            src={match.photo_url || match.main_photo_url}
                            alt={match.display_name || match.profile?.names?.[0] || 'Match'}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="h-full w-full flex items-center justify-center text-sm font-semibold text-white">
                            {(match.display_name || match.profile?.names?.[0] || '?')[0].toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-medium text-gray-900">
                          {match.display_name || match.profile?.names?.join(' & ') || 'Match'}
                        </p>
                        <p className="text-xs text-gray-600">
                          {match.location || match.profile?.location || 'Matched recently'}
                        </p>
                      </div>
                    </div>
                    <MessageCircle className="w-4 h-4 text-rose-600 flex-shrink-0" />
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-sm text-slate-500">
                No matches yet. Visit Quick Match to find your people!
              </div>
            )}
          </div>
        </section>

        {/* Unity Wingman - Compact Card */}
        <section className="mt-8">
          <div className="flex items-center gap-3 rounded-2xl bg-white/90 dark:bg-slate-800/90 shadow-sm px-4 py-3 border border-rose-100 dark:border-slate-600 hover:border-rose-200 dark:hover:border-slate-500 transition-colors">
            {/* Bot icon */}
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-rose-100 flex-shrink-0">
              <span className="text-rose-500 text-xl">🤖</span>
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                Unity Wingman
                <span className="text-amber-400 text-base">✨</span>
              </h3>
              <p className="text-xs text-slate-600 truncate">
                Get AI help with openers and replies while keeping your own vibe.
              </p>
            </div>

            {/* Compact button - navigate to full page */}
            <button
              type="button"
              onClick={() => navigate('/wingman')}
              className="inline-flex items-center rounded-full border border-rose-200 px-3 py-1 text-xs font-medium text-rose-600 hover:bg-rose-50 active:scale-95 transition flex-shrink-0"
              data-testid="wingman-dashboard-open-btn"
            >
              Open
            </button>
          </div>
        </section>

        {/* Share Your LoveLink - Growth Card */}
        <section className="mt-8">
          <LoveLinkGrowthCard />
        </section>

        {/* LoveLink Earnings Teaser - Only shows when user has earnings > $0 */}
        <section className="mt-4">
          <LoveLinkEarningsTeaser />
        </section>
        
        {/* Bottom spacing */}
        <div className="mb-10"></div>
      </div>

      {/* 🎯 Patch B1: Onboarding Tour Modal */}
      <OnboardingTourModal
        open={showOnboarding}
        onClose={handleCloseOnboarding}
      />

      {/* P1: Profile Complete Nudge Modal */}
      <ProfileCompleteNudgeModal
        open={showProfileNudge}
        onClose={() => setShowProfileNudge(false)}
        onComplete={() => {
          setShowProfileNudge(false);
          navigate('/edit-profile');
        }}
      />

      {/* Unity Wingman Modal */}
      <WingmanModal
        open={showWingman}
        onClose={() => setShowWingman(false)}
        token={token}
      />

      {/* LoveLink 3-Step Intro Flow */}
      <LoveLinkIntroFlow
        isOpen={showLoveLinkIntro}
        onClose={handleCloseLoveLinkIntro}
        userName={displayName}
        loveLinkUrl={loveLinkUrl}
      />

      {/* P2: Profile View Modal */}
      <ProfileViewModal
        open={profileViewer.open}
        onClose={profileViewer.close}
        profile={profileViewer.profile}
        isSelf={profileViewer.isSelf}
        source={profileViewer.source}
        onLikeFromProfile={profileViewer.onLikeFromProfile}
        onPassFromProfile={profileViewer.onPassFromProfile}
      />

      {/* Dev-only: Reset tour button */}
      {process.env.NODE_ENV === "development" && (
        <button
          type="button"
          className="fixed bottom-4 right-4 text-[10px] px-2 py-1 bg-gray-900 border border-gray-700 rounded-full text-gray-300 hover:bg-gray-800 z-[10000]"
          onClick={() => {
            localStorage.removeItem("unitylove_onboarding_v1");
            setShowOnboarding(true);
          }}
          title="Reset onboarding tour (dev only)"
        >
          Reset Tour
        </button>
      )}
    </div>
  );
};

// LoveLink Growth Card Component
function LoveLinkGrowthCard() {
  const { user, token } = useContext(AuthContext);
  const [referralData, setReferralData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReferralData = async () => {
      if (!token) return;
      try {
        const res = await apiClient.get(`/referrals/dashboard`);
        // Transform the response to match expected format
        const transformed = {
          referralLink: res.data.referralLink,
          circleSize: (res.data.level1Count || 0) + (res.data.level2Count || 0)
        };
        setReferralData(transformed);
      } catch (err) {
        console.error('Failed to fetch referral data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchReferralData();
  }, [token]);

  const copyLink = async () => {
    const referralLink = referralData?.referralLink;
    if (!referralLink) return;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(referralLink);
      } else {
        // Fallback
        const textarea = document.createElement('textarea');
        textarea.value = referralLink;
        textarea.style.position = 'fixed';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopied(true);
      toast.success('LoveLink copied! Share it with someone you trust ❤️');
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error('Could not copy link. Try again.');
    }
  };

  const shareNative = async () => {
    const referralLink = referralData?.referralLink;
    if (!navigator.share || !referralLink) {
      copyLink();
      return;
    }
    try {
      await navigator.share({
        title: 'Join me on Unity Love',
        text: 'I invited you to Unity Love — a community for all forms of love.',
        url: referralLink,
      });
    } catch (err) {
      // User cancelled, do nothing
      console.log('Share cancelled');
    }
  };

  if (loading) {
    return (
      <div className="glass rounded-2xl p-5 animate-pulse">
        <div className="h-24 bg-gray-200 rounded"></div>
      </div>
    );
  }

  const referralLink = referralData?.referralLink;
  const circleSize = referralData?.circleSize ?? 0;

  return (
    <div className="glass rounded-2xl p-4 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1">
          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
            <Users className="w-5 h-5 text-emerald-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-playfair text-lg font-semibold text-gray-900 mb-1">
              Share Your LoveLink
            </h3>
            <p className="text-sm text-gray-600">
              Invite people you trust. When they join and upgrade, your Circle grows.
            </p>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">
            Your Circle
          </div>
          <div className="flex items-center justify-end gap-1">
            <Users className="w-4 h-4 text-emerald-500" />
            <span className="text-xl font-bold text-emerald-600">
              {circleSize}
            </span>
          </div>
        </div>
      </div>

      {/* Progress bar */}
      {typeof circleSize === 'number' && (
        <div>
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Next milestone</span>
            <span>{Math.max(0, 5 - circleSize)} more to reach 5</span>
          </div>
          <div className="w-full bg-gray-200 h-1.5 rounded-full overflow-hidden">
            <div
              className="h-1.5 bg-emerald-500 rounded-full transition-all duration-300"
              style={{ width: `${Math.min(100, (circleSize / 5) * 100)}%` }}
            />
          </div>
        </div>
      )}

      {/* Compact pill buttons */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={shareNative}
          className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 active:scale-[0.99] transition"
        >
          <span className="text-base">💬</span>
          <span>Share</span>
        </button>
        <button
          type="button"
          onClick={copyLink}
          className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500 dark:border-emerald-400 bg-white dark:bg-slate-800 px-3 py-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-slate-700 transition"
        >
          {copied ? (
            <>
              <Check className="w-3 h-3" />
              <span>Copied!</span>
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" />
              Copy LoveLink
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function MobileNavItem({ icon, label, onClick, badge, badgeColor, muted, amber, testId }) {
  const badgeBg = {
    rose: 'bg-rose-500',
    blue: 'bg-blue-500',
    violet: 'bg-violet-600',
  }[badgeColor] || 'bg-rose-500';

  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={[
        'flex items-center justify-between gap-3 w-full px-4 py-3 transition active:scale-[0.98]',
        amber
          ? 'text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/15'
          : muted
            ? 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-800'
            : 'text-gray-700 dark:text-gray-200 hover:bg-rose-50 dark:hover:bg-slate-800',
      ].join(' ')}
    >
      <span className="flex items-center gap-3">
        {icon}
        <span className="text-sm font-medium">{label}</span>
      </span>
      {badge != null && badge !== '' && (
        <span className={`min-w-[20px] h-5 px-1.5 rounded-full ${badgeBg} text-white text-[11px] font-bold flex items-center justify-center leading-none`}>
          {badge}
        </span>
      )}
    </button>
  );
}

export default Dashboard;